import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback
} from "react";
import { useSelector } from "react-redux";
import {
  selectCuvamaDiscoveryLink,
  selectDiscoveryId,
  selectUser
} from "../store/reducers";
import { useFeature } from "../components/FeaturesProvider";
import Features from "../utils/constants/Features";
import IntegrationNames from "../utils/constants/integration-names";
import pusherService from "../services/pusher.service";
import httpService from "../services/http.service";
import ChannelNames from "../utils/channel-names";
import PusherEventNames from "../utils/constants/pusher-event-names";
import { RoleCodes } from "../utils/role-codes";
import { useIntegration, IntegrationsContext } from "./integrationsProvider";
import type { ApiTypes } from "cuvama-api";

interface CuvamaDiscoveryLinkContextType {
  cuvamaDiscoveryLink: ApiTypes.CuvamaDiscoveryLink | null;
  shouldShowDiscoveryLinkInNav: boolean;
}

const CuvamaDiscoveryLinkContext =
  createContext<CuvamaDiscoveryLinkContextType>({
    cuvamaDiscoveryLink: null,
    shouldShowDiscoveryLinkInNav: false
  });

export const useCuvamaDiscoveryLink = () =>
  useContext(CuvamaDiscoveryLinkContext);

interface CuvamaDiscoveryLinkProviderProps {
  children: React.ReactNode;
}

const CuvamaDiscoveryLinkProvider = ({
  children
}: CuvamaDiscoveryLinkProviderProps) => {
  const reduxLink = useSelector(selectCuvamaDiscoveryLink);
  const discoveryId = useSelector(selectDiscoveryId);
  const cuvamaDiscoveryEnabled = useFeature(
    Features.CUVAMA_DISCOVERY_INTEGRATION,
    false
  );

  const integration = useIntegration(IntegrationNames.CUVAMA_DISCOVERY);
  const isConnected = integration?.connected ?? false;
  const { refreshIntegration } = useContext(IntegrationsContext);

  const user = useSelector(selectUser);
  const userRoleCodes: string[] =
    user?.roles?.map((role: { code: string }) => role.code) ?? [];
  const isExternalOrLeadGenUser =
    userRoleCodes.includes(RoleCodes.EXTERNAL_USER) ||
    userRoleCodes.includes(RoleCodes.LEAD_GEN_USER);

  const [cuvamaDiscoveryLink, setCuvamaDiscoveryLink] =
    useState<ApiTypes.CuvamaDiscoveryLink | null>(reduxLink);

  useEffect(() => {
    if (cuvamaDiscoveryEnabled) {
      refreshIntegration(IntegrationNames.CUVAMA_DISCOVERY);
    }
  }, [cuvamaDiscoveryEnabled, refreshIntegration]);

  useEffect(() => {
    setCuvamaDiscoveryLink(reduxLink);
  }, [reduxLink]);

  const onSyncStatusUpdate = useCallback(
    (data: { cuvamaDiscoveryLink: ApiTypes.CuvamaDiscoveryLink }) => {
      if (data?.cuvamaDiscoveryLink) {
        setCuvamaDiscoveryLink(data.cuvamaDiscoveryLink);
      }
    },
    []
  );

  useEffect(() => {
    if (!cuvamaDiscoveryEnabled || !discoveryId) {
      return;
    }

    const tenantId = httpService.getTenantId();
    const channelName = ChannelNames.DISCOVERY({ discoveryId, tenantId });

    pusherService.subscribe({
      channelName,
      eventName: PusherEventNames.UPDATE_SYNC_STATUS,
      callback: onSyncStatusUpdate
    });

    return () => {
      pusherService.unsubscribe({
        channelName,
        eventName: PusherEventNames.UPDATE_SYNC_STATUS,
        callback: onSyncStatusUpdate
      });
    };
  }, [cuvamaDiscoveryEnabled, discoveryId, onSyncStatusUpdate]);

  const shouldShowDiscoveryLinkInNav =
    cuvamaDiscoveryEnabled &&
    isConnected &&
    cuvamaDiscoveryLink !== null &&
    !isExternalOrLeadGenUser;

  return (
    <CuvamaDiscoveryLinkContext.Provider
      value={{ cuvamaDiscoveryLink, shouldShowDiscoveryLinkInNav }}
    >
      {children}
    </CuvamaDiscoveryLinkContext.Provider>
  );
};

export default CuvamaDiscoveryLinkProvider;
